<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" default-href="/admin-promo-codes"></ion-back-button>
        </ion-buttons>
        <ion-title>{{ isNew ? 'Новый промокод' : 'Промокод' }}</ion-title>
        <ion-buttons slot="end">
          <HeaderMessengerButton />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true">
      <div class="ion-padding">
        <ion-item lines="none">
          <ion-input v-model="form.code" label="Код" label-placement="stacked" placeholder="SUMMER25" :disabled="!isNew && saving" />
        </ion-item>
        <ion-item lines="none">
          <ion-input
            v-model.number="form.percentOff"
            type="number"
            label="Скидка %"
            label-placement="stacked"
            min="1"
            max="100"
          />
        </ion-item>
        <ion-item lines="none">
          <ion-input v-model="validFromLocal" type="datetime-local" label="Действует с" label-placement="stacked" />
        </ion-item>
        <ion-item lines="none">
          <ion-input v-model="validToLocal" type="datetime-local" label="Действует до" label-placement="stacked" />
        </ion-item>
        <ion-item lines="none">
          <ion-input
            v-model.number="form.maxRedemptions"
            type="number"
            label="Лимит использований (пусто = без лимита)"
            label-placement="stacked"
          />
        </ion-item>
        <ion-item lines="none">
          <ion-input
            v-model.number="form.maxPerUser"
            type="number"
            label="Лимит на пользователя"
            label-placement="stacked"
            min="1"
          />
        </ion-item>
        <ion-item lines="none">
          <ion-select
            v-model="form.targetType"
            label="Область"
            label-placement="stacked"
            interface="action-sheet"
            cancel-text="Отмена"
            @ionChange="onTargetTypeChange"
          >
            <ion-select-option value="SUBSCRIPTION">Подписки</ion-select-option>
            <ion-select-option value="EVENT">События</ion-select-option>
            <ion-select-option value="ALL">Подписки и события</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item v-if="form.targetType === 'SUBSCRIPTION'" lines="none">
          <ion-select
            v-model="form.subscriptionId"
            label="Подписка"
            label-placement="stacked"
            interface="action-sheet"
            cancel-text="Отмена"
            placeholder="Все подписки"
          >
            <ion-select-option :value="null">Все подписки</ion-select-option>
            <ion-select-option v-for="s in subscriptions" :key="s.id" :value="s.id">{{ subscriptionLabel(s) }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item v-if="form.targetType === 'EVENT'" lines="none">
          <ion-select
            v-model="form.eventId"
            label="Событие"
            label-placement="stacked"
            interface="action-sheet"
            cancel-text="Отмена"
            placeholder="Все события"
          >
            <ion-select-option :value="null">Все события</ion-select-option>
            <ion-select-option v-for="e in events" :key="e.id" :value="e.id">{{ e.title }}</ion-select-option>
          </ion-select>
        </ion-item>
        <ion-item lines="none">
          <ion-toggle v-model="form.active">Активен</ion-toggle>
        </ion-item>
        <ion-button expand="block" class="event-register-btn" :disabled="saving" @click="save">
          {{ saving ? 'Сохранение…' : 'Сохранить' }}
        </ion-button>
        <ion-button
          v-if="!isNew"
          expand="block"
          fill="outline"
          color="danger"
          :disabled="saving"
          @click="remove"
        >
          Удалить
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToggle,
  IonToolbar,
  toastController,
  alertController
} from '@ionic/vue';
import { computed, defineComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import promoCodesService, { ILigaPromoCode } from '@/services/promo-codes.service';
import subscriptionsService from '@/services/subscriptions.service';
import eventsService from '@/services/events.service';
import type { ILigaSubscription } from '@/model/liga-subscription.model';
import type { ILigaEvent } from '@/model/liga-event.model';
import HeaderMessengerButton from '@/components/HeaderMessengerButton.vue';

function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local: string): string | null {
  if (!local?.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default defineComponent({
  name: 'AdminPromoCodeEdit',
  components: {
    HeaderMessengerButton,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToggle,
    IonToolbar
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isNew = computed(() => route.name === 'AdminPromoCodeNew' || route.params.id === 'new');
    return { route, router, isNew };
  },
  data() {
    return {
      form: {
        code: '',
        percentOff: 10,
        validFrom: null as string | null,
        validTo: null as string | null,
        active: true,
        maxRedemptions: null as number | null,
        maxPerUser: 1,
        targetType: 'SUBSCRIPTION' as ILigaPromoCode['targetType'],
        subscriptionId: null as number | null,
        eventId: null as number | null
      } as ILigaPromoCode,
      validFromLocal: '',
      validToLocal: '',
      subscriptions: [] as ILigaSubscription[],
      events: [] as ILigaEvent[],
      saving: false
    };
  },
  async mounted() {
    const [subs, evs] = await Promise.all([
      subscriptionsService.getSubscriptions({ all: true }),
      eventsService.getEvents({ all: true })
    ]);
    this.subscriptions = [...subs].sort((a, b) => {
      const byTitle = String(a.title || '').localeCompare(String(b.title || ''), 'ru');
      if (byTitle !== 0) return byTitle;
      return (a.durationDays ?? 0) - (b.durationDays ?? 0);
    });
    this.events = evs;
    if (!this.isNew) {
      const id = Number(this.route.params.id);
      if (!id) return;
      const promo = await promoCodesService.get(id);
      this.form = {
        ...promo,
        targetType: promo.targetType || 'SUBSCRIPTION',
        subscriptionId: promo.subscriptionId ?? null,
        eventId: promo.eventId ?? null,
        maxRedemptions: promo.maxRedemptions ?? null,
        maxPerUser: promo.maxPerUser ?? 1,
        active: promo.active !== false
      };
      this.validFromLocal = toLocalInput(promo.validFrom);
      this.validToLocal = toLocalInput(promo.validTo);
    }
  },
  methods: {
    durationWord(days: number): string {
      const v = days % 10;
      const v2 = days % 100;
      if (v2 >= 11 && v2 <= 14) return 'дней';
      if (v === 1) return 'день';
      if (v >= 2 && v <= 4) return 'дня';
      return 'дней';
    },
    subscriptionLabel(s: ILigaSubscription): string {
      const title = s.title || `Подписка #${s.id}`;
      if (s.durationDays != null && s.durationDays > 0) {
        return `${title} · ${s.durationDays} ${this.durationWord(s.durationDays)}`;
      }
      return `${title} · бессрочная`;
    },
    onTargetTypeChange() {
      if (this.form.targetType === 'SUBSCRIPTION') {
        this.form.eventId = null;
      } else if (this.form.targetType === 'EVENT') {
        this.form.subscriptionId = null;
      } else {
        this.form.subscriptionId = null;
        this.form.eventId = null;
      }
    },
    async save() {
      this.saving = true;
      try {
        const payload: ILigaPromoCode = {
          ...this.form,
          code: String(this.form.code || '').trim().toUpperCase(),
          percentOff: Number(this.form.percentOff),
          maxPerUser: Number(this.form.maxPerUser) || 1,
          maxRedemptions: this.form.maxRedemptions ? Number(this.form.maxRedemptions) : null,
          validFrom: fromLocalInput(this.validFromLocal),
          validTo: fromLocalInput(this.validToLocal),
          targetType: this.form.targetType || 'SUBSCRIPTION',
          subscriptionId: this.form.targetType === 'SUBSCRIPTION' ? this.form.subscriptionId || null : null,
          eventId: this.form.targetType === 'EVENT' ? this.form.eventId || null : null
        };
        if (this.isNew) {
          await promoCodesService.create(payload);
        } else {
          await promoCodesService.update(Number(this.route.params.id), payload);
        }
        const toast = await toastController.create({ message: 'Сохранено', color: 'success', duration: 1800 });
        await toast.present();
        this.$router.push('/admin-promo-codes');
      } catch (e: any) {
        const data = e?.response?.data ?? e?.data;
        const msg = data?.detail || data?.message || e?.message || 'Ошибка сохранения';
        const toast = await toastController.create({ message: String(msg), color: 'warning', duration: 3500 });
        await toast.present();
      }
      this.saving = false;
    },
    async remove() {
      const alert = await alertController.create({
        header: 'Удалить промокод?',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: () => {
              void this.doRemove();
            }
          }
        ]
      });
      await alert.present();
    },
    async doRemove() {
      this.saving = true;
      try {
        await promoCodesService.remove(Number(this.route.params.id));
        this.$router.push('/admin-promo-codes');
      } catch (e: any) {
        const toast = await toastController.create({
          message: e?.message || 'Не удалось удалить',
          color: 'danger',
          duration: 3000
        });
        await toast.present();
      }
      this.saving = false;
    }
  }
});
</script>

<style scoped>
.event-register-btn {
  --background: #e85d04;
  --color: var(--liga-on-accent);
  font-weight: 600;
  margin-top: 16px;
}
</style>
